<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input
          v-model="query.keywords"
          placeholder="订单号 / 用户名 / 联系方式"
          clearable
          style="width:220px"
          @keyup.enter.native="handleSearch"
        />
        <el-select v-model="query.status" placeholder="订单状态" clearable style="width:130px">
          <el-option label="待付款" :value="0" />
          <el-option label="已付款" :value="1" />
          <el-option label="部分退款" :value="2" />
          <el-option label="已取消" :value="3" />
          <el-option label="已退款" :value="4" />
          <el-option label="已完成" :value="5" />
        </el-select>
        <el-select v-model="query.refund_apply_status" placeholder="退款申请" clearable style="width:120px">
          <el-option label="未申请" :value="0" />
          <el-option label="已申请" :value="1" />
        </el-select>
        <el-select v-model="query.invoice_status" placeholder="开票状态" clearable style="width:130px">
          <el-option label="未申请" :value="-1" />
          <el-option label="已申请待处理" :value="0" />
          <el-option label="已处理" :value="1" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe style="width:100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="order_no" label="产品订单号" min-width="190" show-overflow-tooltip />
        <el-table-column label="订单状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusType(row.status)" size="small">
              {{ row.status_label || statusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="开票状态" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag
              v-if="row.invoice_status !== undefined && row.invoice_status !== -1"
              :type="row.invoice_status === 1 ? 'success' : 'warning'"
              size="small"
            >
              {{ row.invoice_status_label || invoiceStatusLabel(row.invoice_status) }}
            </el-tag>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="buyer_name" label="客户姓名" width="100" />
        <el-table-column prop="buyer_phone" label="手机号" width="130" />
        <el-table-column prop="created_at" label="下单时间" width="160" />
        <el-table-column prop="product_name" label="产品名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="quantity" label="数量" width="60" align="center" />
        <el-table-column label="实付金额" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="val-price">¥{{ toFixed2(row.pay_amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['order:product:detail']"
              type="primary" size="mini" icon="el-icon-view"
              @click="$router.push(`/order/product/detail?id=${row.id}`)"
            >详情</el-button>
            <el-button
              v-if="row.invoice_status !== undefined && row.invoice_status !== -1"
              type="warning" size="mini" icon="el-icon-document"
              @click="handleViewInvoice(row)"
            >发票</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:16px;text-align:right"
        @size-change="val => { query.pageSize = val; fetchData() }"
        @current-change="val => { query.page = val; fetchData() }"
      />
    </el-card>

    <!-- 发票详情弹框 -->
    <el-dialog
      title="发票详情"
      :visible.sync="invoiceDialog.visible"
      width="520px"
      :close-on-click-modal="false"
      @closed="resetInvoiceDialog"
    >
      <div v-loading="invoiceDialog.loading">
        <!-- 已处理警告 -->
        <el-alert
          v-if="invoiceDialog.data && invoiceDialog.data.invoice_status === 1"
          title="该发票已开过票"
          type="warning"
          show-icon
          :closable="false"
          style="margin-bottom:16px"
        />

        <template v-if="invoiceDialog.data && invoiceDialog.data.invoice">
          <el-descriptions :column="1" border size="small" class="invoice-desc">
            <el-descriptions-item label="发票类型">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.type_label)">
                {{ invoiceDialog.data.invoice.type_label || '—' }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="抬头名称">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.title)">
                {{ invoiceDialog.data.invoice.title || '—' }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.tax_no" label="税号">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.tax_no)">
                {{ invoiceDialog.data.invoice.tax_no }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.address" label="单位地址">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.address)">
                {{ invoiceDialog.data.invoice.address }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.telephone" label="公司电话">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.telephone)">
                {{ invoiceDialog.data.invoice.telephone }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.bank_name" label="开户银行">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.bank_name)">
                {{ invoiceDialog.data.invoice.bank_name }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.bank_account" label="银行账号">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.bank_account)">
                {{ invoiceDialog.data.invoice.bank_account }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.email" label="接收邮箱">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.email)">
                {{ invoiceDialog.data.invoice.email }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.remark" label="备注">
              <span class="copy-field" @click="copyText(invoiceDialog.data.invoice.remark)">
                {{ invoiceDialog.data.invoice.remark }}
                <i class="el-icon-copy-document copy-icon" />
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="申请时间">
              {{ invoiceDialog.data.invoice.applied_at || '—' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="invoiceDialog.data.invoice.finished_at" label="处理时间">
              {{ invoiceDialog.data.invoice.finished_at }}
            </el-descriptions-item>
          </el-descriptions>
        </template>
        <el-empty v-else-if="!invoiceDialog.loading" description="暂无发票信息" :image-size="60" />
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button
          v-if="invoiceDialog.data && invoiceDialog.data.invoice_status === 0"
          type="primary"
          :loading="invoiceDialog.marking"
          @click="confirmMarkProcessed"
        >标记为已处理</el-button>
        <el-button @click="invoiceDialog.visible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProductOrderList, getProductOrderInvoiceDetail, markInvoiceProcessed } from '@/api/order'

const ORDER_STATUS = {
  0: { label: '待付款', type: 'warning' },
  1: { label: '已付款', type: 'success' },
  2: { label: '部分退款', type: '' },
  3: { label: '已取消', type: 'info' },
  4: { label: '已退款', type: 'danger' },
  5: { label: '已完成', type: 'success' }
}

const INVOICE_STATUS_MAP = {
  '-1': '未申请',
  '0': '已申请待处理',
  '1': '已处理'
}

export default {
  name: 'ProductOrderList',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keywords: '', status: '', refund_apply_status: '', invoice_status: '' },
      invoiceDialog: {
        visible: false,
        loading: false,
        marking: false,
        orderId: null,
        data: null
      }
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/order/product') return
        const refundStatus = to.query.refund_apply_status
        const invoiceStatus = to.query.invoice_status
        this.query.refund_apply_status = refundStatus !== undefined ? Number(refundStatus) : ''
        this.query.invoice_status = invoiceStatus !== undefined ? Number(invoiceStatus) : ''
        this.query.page = 1
        this.fetchData()
      }
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.status === '') delete params.status
        if (params.refund_apply_status === '') delete params.refund_apply_status
        if (params.invoice_status === '') delete params.invoice_status
        if (!params.keywords) delete params.keywords
        const res = await getProductOrderList(params)
        const d = res.data || {}
        this.tableData = d.list || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() {
      this.query = { page: 1, pageSize: 15, keywords: '', status: '', refund_apply_status: '', invoice_status: '' }
      this.fetchData()
    },
    statusType(s) { return (ORDER_STATUS[s] || {}).type || '' },
    statusLabel(s) { return (ORDER_STATUS[s] || {}).label || String(s) },
    invoiceStatusLabel(s) { return INVOICE_STATUS_MAP[String(s)] || String(s) },
    toFixed2(val) { return Number(val || 0).toFixed(2) },

    async handleViewInvoice(row) {
      this.invoiceDialog.visible = true
      this.invoiceDialog.loading = true
      this.invoiceDialog.orderId = row.id
      this.invoiceDialog.data = null
      try {
        const res = await getProductOrderInvoiceDetail({ order_id: row.id })
        this.invoiceDialog.data = res.data || null
      } catch (e) {
        this.$message.error('获取发票信息失败')
      } finally {
        this.invoiceDialog.loading = false
      }
    },

    confirmMarkProcessed() {
      this.$confirm('确认将该发票标记为已处理？此操作不可撤销。', '提示', {
        confirmButtonText: '确认标记',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.handleMarkProcessed()
      }).catch(() => {})
    },

    async handleMarkProcessed() {
      if (!this.invoiceDialog.data || !this.invoiceDialog.data.invoice) return
      this.invoiceDialog.marking = true
      try {
        await markInvoiceProcessed({ invoice_id: this.invoiceDialog.data.invoice.id })
        this.$message.success('已标记为已处理')
        this.invoiceDialog.data.invoice_status = 1
        this.invoiceDialog.data.invoice_status_label = '已处理'
        const row = this.tableData.find(r => r.id === this.invoiceDialog.orderId)
        if (row) {
          row.invoice_status = 1
          row.invoice_status_label = '已处理'
        }
      } catch (e) {
        this.$message.error('操作失败，请重试')
      } finally {
        this.invoiceDialog.marking = false
      }
    },

    resetInvoiceDialog() {
      this.invoiceDialog.data = null
      this.invoiceDialog.orderId = null
    },

    copyText(text) {
      if (!text) return
      const input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message({ message: '已复制', type: 'success', duration: 1200 })
    }
  }
}
</script>

<style lang="scss" scoped>
.search-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.val-price   { color: #f56c6c; font-weight: 600; }
.text-muted  { color: #c0c4cc; font-size: 13px; }

.invoice-desc {
  ::v-deep .el-descriptions-item__label {
    width: 90px;
    background: #fafafa;
  }
}

.copy-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 4px;
  transition: background 0.15s;
  &:hover {
    background: #f0f7ff;
    .copy-icon { opacity: 1; }
  }
  .copy-icon {
    font-size: 13px;
    color: #409eff;
    opacity: 0;
    transition: opacity 0.15s;
  }
}
</style>
